import { Input, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import WebinarView from "../../../components/admin/ManageEvents/WebinarView";
import FilterSection from "../../../components/common/FilterSection";
import ActionButton from "../../../components/ui/ActionButton";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import CustomTable from "../../../components/ui/CustomTable";
import useDebounce from "../../../hooks/useDebounce";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import { getAllDiscussionBoardAsync } from "../../../store/features/discussionBoard/discussionBoardApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { TableData } from "../../../types";
import { GetDiscussionBoardData } from "../../../types/discussionBoard/discussionBoard";
import { countRow } from "../../../utils/number";
import { addKeyInArray, changeStatus, skipData } from "../../../utils/utils";

type Status = "Pending" | "Published" | "Completed";

const dealerStatusClass = {
  Published: "status-waiting",
  Pending: "status-pending",
  Completed: "status-done",
};

export default function DiscusPendingRequests() {
  const dispatch = useAppDispatch();
  const { keyword, handleInputChange } = useDebounce();
  const { open, toggle, handleOpen } = useModal(reRender);
  const { limit, page, handlePagination, handleLimit, resetPagination } =
    useTable();

  const [selectedData, setSelectedData] = useState<any[]>([]);

  const { discussionBoardList, countDiscussionBoard, loading } = useAppSelector(
    (state) => state.discussionBoard
  );

  const columns: ColumnsType<TableData<GetDiscussionBoardData>> = [
    {
      title: "#SL",
      align: "center",
      render: (_, __, idx) => countRow(page, limit, idx),
    },
    {
      title: "Discussion Title",
      width: 482,
      render: (val) => (
        <div className="py-1.5 flex-col justify-start items-start gap-2 inline-flex">
          <div className="text-[#dc3545] text-lg font-semibold leading-normal tracking-wide">
            {val.discussionBoardTitle}
          </div>

          <div className="justify-start items-center gap-4 inline-flex">
            <div className="justify-start items-center gap-2 flex">
              <div className="w-3 h-3 bg-[#ea868f]" />
              <div className="text-[#495057] text-sm font-medium leading-snug tracking-wide">
                {val.categoryId?.categoryName}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Created By",
      dataIndex: "discussionBoardCreatedBy",
      render: (val) => <span>{val.fullName || "N/A"}</span>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      render: (val) => (
        <div>
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "discussionBoardStatus",
      render: (val: Status) => (
        <span className={dealerStatusClass[val]}>{changeStatus(val)}</span>
      ),
    },
    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val) => (
        <Space size="small">
          {/* <ActionButton
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val,
              })
            }
          /> */}
          <Link to={`/admin/discussion-board/pending-requests/${val._id}`}>
            <ActionButton icon={<ViewIcon />} />
          </Link>
          {/* <ActionButton
            icon={<DeleteIcon color="#AFBACA" />}
            onClick={() =>
              handleOpen({
                type: "DELETE",
                data: val,
              })
            }
          /> */}
        </Space>
      ),
    },
  ];

  // item delete handler
  const handleDelete = (id: string) => {};

  const getData = useCallback(() => {
    dispatch(
      getAllDiscussionBoardAsync({
        params: {
          skip: skipData(page, limit),
          limit,
          discussionBoardStatus: "Pending",
          custom_text: keyword,
          categoryId: "",
          discussionBoardCreatedById: "",
          regional_hub_id: "",
        },
      })
    );
  }, [dispatch, keyword, limit, page]);

  useEffect(() => {
    getData();
  }, [getData]);

  function reRender() {
    getData();
  }

  return (
    <AdminLayout title="Pending Request" className="dashboard">
      <CustomTable<TableData<GetDiscussionBoardData>>
        loading={loading}
        total={countDiscussionBoard}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        handleLimit={handleLimit}
        dataList={addKeyInArray(discussionBoardList)}
        columns={columns}
        bordered={false}
        isLimitRow
        showTotalPage
        rowSelection={{
          type: "checkbox",
          onChange: (_, data) => setSelectedData(data),
          columnWidth: 45,
        }}
        tableTopChildren={
          <FilterSection>
            <Input
              size="large"
              suffix={<SearchIcon />}
              placeholder={`Search within ${countDiscussionBoard} data`}
              onChange={(e) => {
                handleInputChange(e);
                resetPagination();
              }}
            />
          </FilterSection>
        }
      />

      {open?.type === "VIEW" && (
        <WebinarView open={open} handleClose={toggle} />
      )}

      {/* Category delete modal */}
      {open?.type === "DELETE" && (
        <ConfirmModal
          open={open.open}
          data={open?.data}
          deleteLoading={false}
          handleYes={handleDelete}
          handleClose={toggle}
        />
      )}
    </AdminLayout>
  );
}
